import { useEffect, useState } from 'react';
import { useTenantFamilyNotes } from '@tark/shared/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, Textarea } from '@tark/shared/components';
import type { TenantFamilyAthleteSummary, TenantFamilyNote, TenantFamilyNoteKind, TenantFamilyScheduleSession } from '@tark/shared/types';
import { MessageSquarePlus, Pencil, Sparkles, Trash2 } from 'lucide-react';

const GOALS = ['Confidence', 'Consistency', 'Technique', 'Fitness', 'Match play', 'Enjoyment'];

export function FamilyNotesPanel({ athlete, sessions, enabled }: {
  athlete: TenantFamilyAthleteSummary | null;
  sessions: TenantFamilyScheduleSession[];
  enabled: boolean;
}) {
  const athleteId = athlete?.athleteId ?? null;
  const notes = useTenantFamilyNotes(athleteId, enabled);
  const [editor, setEditor] = useState<{ intake: boolean; note: TenantFamilyNote | null } | null>(null);
  const [kind, setKind] = useState<TenantFamilyNoteKind>('GOAL');
  const [text, setText] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [occurrenceId, setOccurrenceId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setEditor(null); setError(null); }, [athleteId]);

  const openEditor = (intake: boolean, note: TenantFamilyNote | null = null) => {
    setEditor({ intake, note });
    setKind(intake ? 'INTAKE' : note?.kind === 'INTAKE' ? 'GOAL' : note?.kind ?? 'GOAL');
    setText(note?.text ?? '');
    setTags(note?.tags ?? []);
    setOccurrenceId(note?.occurrenceId ? String(note.occurrenceId) : '');
    setError(null);
  };

  if (!athlete) return null;
  const relevantSessions = sessions.filter((session) =>
    session.athleteBookings.some((booking) => booking.athleteId === athlete.athleteId)
  );

  const save = async () => {
    if (!editor) return;
    const body = text.trim() || (tags.length ? `Goals: ${tags.join(', ')}` : '');
    if (!body) { setError('Add a short note or choose at least one goal.'); return; }
    const session = relevantSessions.find((item) => String(item.occurrenceId) === occurrenceId);
    try {
      await notes.saveNote({ noteId: editor.note?.noteId ?? null, payload: {
        kind: editor.intake ? 'INTAKE' : kind,
        text: body,
        tags,
        occurrenceId: session?.occurrenceId ?? null,
        programId: session?.programId ?? null,
        version: editor.note?.version ?? null,
      }});
      setEditor(null);
    } catch { setError('Could not save this note. Refresh and try again.'); }
  };

  return (
    <Card variant="default">
      <CardHeader className="flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><MessageSquarePlus className="h-5 w-5 text-primary" /> Notes for coach</CardTitle>
          <p className="mt-1 text-xs text-text-muted">Private to this family and coaching team.</p>
        </div>
        <Button size="sm" onClick={() => openEditor(false)}>Add note</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {notes.timeline?.intake.status === 'PENDING' ? (
          <div className="rounded-xl border border-primary/25 bg-primary/5 p-3">
            <div className="flex items-start gap-3"><Sparkles className="mt-0.5 h-4 w-4 text-primary" /><div className="min-w-0 flex-1"><p className="text-sm font-semibold text-text">Help the coach start well</p><p className="mt-1 text-xs leading-5 text-text-muted">Share an optional goal before the next session.</p></div></div>
            <div className="mt-3 flex gap-2"><Button size="sm" onClick={() => openEditor(true)}>Share goals</Button><Button size="sm" variant="ghost" onClick={() => notes.skipIntake()}>Not now</Button></div>
          </div>
        ) : null}

        {editor ? (
          <div className="space-y-3 rounded-xl border border-border bg-surface-elevated/20 p-3">
            {!editor.intake ? <div className="grid grid-cols-2 gap-2 rounded-lg bg-surface p-1">{([['GOAL', 'Goal or need'], ['OBSERVATION', 'Match observation']] as const).map(([value, label]) => <button key={value} type="button" onClick={() => setKind(value)} className={`min-h-10 rounded-md px-3 text-xs font-semibold ${kind === value ? 'border border-border bg-surface-elevated text-primary' : 'text-text-muted'}`}>{label}</button>)}</div> : null}
            {(editor.intake || kind === 'GOAL') ? <div className="flex flex-wrap gap-2">{GOALS.map((goal) => <button key={goal} type="button" onClick={() => setTags((current) => current.includes(goal) ? current.filter((item) => item !== goal) : [...current, goal])} className={`min-h-9 rounded-full border px-3 text-xs font-semibold ${tags.includes(goal) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-text-muted'}`}>{goal}</button>)}</div> : null}
            <Textarea label="Note" value={text} maxLength={1200} rows={4} onChange={(event) => setText(event.target.value)} />
            {!editor.intake && relevantSessions.length ? <select value={occurrenceId} onChange={(event) => setOccurrenceId(event.target.value)} className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm text-text"><option value="">No linked session</option>{relevantSessions.slice(0, 12).map((session) => <option key={session.occurrenceId} value={session.occurrenceId}>{formatSession(session)}</option>)}</select> : null}
            {error ? <p className="text-sm text-red-500">{error}</p> : null}
            <div className="flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={() => setEditor(null)}>Cancel</Button><Button size="sm" isLoading={notes.isMutating} onClick={save}>Save</Button></div>
          </div>
        ) : null}

        {notes.isLoading ? <p className="text-sm text-text-muted">Loading notes…</p> : null}
        {!notes.isLoading && (notes.timeline?.notes.length ?? 0) === 0 ? <p className="text-sm text-text-muted">Goals or match observations will appear here.</p> : null}
        {notes.timeline?.notes.map((note) => <div key={note.noteId} className="rounded-xl border border-border bg-surface-elevated/20 p-3">
          <div className="flex items-start justify-between gap-3"><div><p className="text-sm font-semibold text-text">{formatKind(note.kind)}</p><p className="mt-1 text-xs text-text-muted">{[note.authorLabel || 'Family', formatDate(note.createdAt), note.occurrenceTitle].filter(Boolean).join(' • ')}</p></div>{note.authoredByViewer ? <div className="flex gap-1"><button className="grid h-10 w-10 place-items-center rounded-full text-text-muted hover:bg-surface" onClick={() => openEditor(false, note)} aria-label="Edit note"><Pencil className="h-4 w-4" /></button><button className="grid h-10 w-10 place-items-center rounded-full text-red-500 hover:bg-red-500/10" onClick={() => { if (window.confirm('Delete this note?')) void notes.deleteNote({ noteId: note.noteId, version: note.version }); }} aria-label="Delete note"><Trash2 className="h-4 w-4" /></button></div> : null}</div>
          <p className="mt-2 text-sm leading-6 text-text">{note.text}</p>
        </div>)}
      </CardContent>
    </Card>
  );
}

function formatKind(kind: string) { return kind === 'OBSERVATION' ? 'Match observation' : kind === 'INTAKE' ? 'Starting goals' : 'Goal or need'; }
function formatDate(value?: string | null) { if (!value) return ''; const date = new Date(value); return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
function formatSession(session: TenantFamilyScheduleSession) { return `${formatDate(session.startsAt)} · ${session.title || session.programName || 'Session'}`; }
